import { useMemo } from "react";
import { skipToken } from "@reduxjs/toolkit/query";
import { HttpStatusCode } from "axios";
import { isFetchBaseQueryError } from "_shared/_errors/api-error-message";
import type { ProposalCarriedOverNegotiation } from "work/proposals/negotiation/proposal-carried-over-negotiation-context";
import { useGetProposalCollaborationQuery } from "work/proposals/negotiation/proposal-collaboration-api";

/** The negotiation a revision continues, as the reader may read it. */
export type RevisedNegotiation = {
  /** Its negotiation id, once its envelope loaded: what the Comments pane loads its conversation by. */
  negotiationId?: string;
  /** The reader is refused it (#6631), so the Comments pane says the earlier conversation is not theirs. */
  refused: boolean;
  /** What each field's history reads it under. See `ProposalCarriedOverNegotiationContext`. */
  fieldHistory?: ProposalCarriedOverNegotiation;
};

/**
 * The negotiation a proposal revises, when it revises one.
 *
 * A revision of hired work opens on terms both parties already agreed, and the conversation and change
 * history behind those terms belong to the negotiation that agreed them. Its envelope is read here only
 * for its negotiation id -- the Comments pane needs that to load its conversation, and each field's
 * history to ask for that field's earlier changes -- under a cache scope of its own, so the prior
 * negotiation never lands in the store as this viewer's live one and its (absent) pending changes are
 * never previewed against the page.
 *
 * A reader the prior negotiation does not admit gets a rejected query. The Comments pane then says the
 * earlier conversation is not available to them, rather than drawing nothing, which read as nothing
 * having been said (#6631).
 *
 * The active and archived pages both read it here (#7207). Each once derived it inline, and the archived
 * page's Comments pane went without it: a revision that was hired or ended dropped everything said
 * before it.
 */
export function useRevisedNegotiation(revisesProposalId: string | undefined): RevisedNegotiation {
  const viewerScope = revisesProposalId ? `revised-negotiation:${revisesProposalId}` : undefined;
  const query = useGetProposalCollaborationQuery(
    revisesProposalId && viewerScope ? { proposalId: revisesProposalId, viewerScope } : skipToken,
  );
  const negotiationId = query.data?.negotiationId;
  const refused =
    Boolean(revisesProposalId) &&
    isFetchBaseQueryError(query.error) &&
    query.error.status === HttpStatusCode.Forbidden;
  const fieldHistory = useMemo<ProposalCarriedOverNegotiation | undefined>(
    () => (negotiationId && viewerScope ? { negotiationId, viewerScope } : undefined),
    [negotiationId, viewerScope],
  );
  return { negotiationId, refused, fieldHistory };
}
